import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from './AuthContext';

const OrgContext = createContext(null);

export function OrgProvider({ children }) {
  const { user } = useAuth();
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMembership = useCallback(async () => {
    if (!user) {
      setMembership(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from('organisation_members')
      .select('org_id, role, organisations(id, name)')
      .eq('user_id', user.id)
      .single();

    if (data && !error) {
      const m = {
        org_id: data.org_id,
        role: data.role,
        org_name: data.organisations?.name,
      };
      setMembership(m);
      try { localStorage.setItem(`gc_membership_${user.id}`, JSON.stringify(m)); } catch {}
    } else {
      // Distinguish "offline / network error" from "online, confirmed no org"
      const cached = (() => {
        try { return JSON.parse(localStorage.getItem(`gc_membership_${user.id}`)); } catch { return null; }
      })();
      if (!navigator.onLine && cached) {
        // Offline with a previously-confirmed membership — use the cache.
        setMembership(cached);
      } else {
        // Online and no membership row found — genuine new user or removed from org.
        try { localStorage.removeItem(`gc_membership_${user.id}`); } catch {}
        setMembership(null);
      }
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchMembership();
  }, [fetchMembership]);

  const isAdmin = membership?.role === 'admin' || membership?.role === 'owner';
  const isOwner = membership?.role === 'owner';

  return (
    <OrgContext.Provider value={{ membership, loading, isAdmin, isOwner, refetch: fetchMembership }}>
      {children}
    </OrgContext.Provider>
  );
}

export const useOrg = () => useContext(OrgContext);
